// AdminWsListener.jsx
import { useEffect, useRef } from "react";
import { notification } from "antd";
import { API_BASE_URL, WS_ENDPOINT_URL } from "configs/AppConfig";
import { ensureWS, subscribe } from "./wsClient";
import { useAdminNotifications } from "contexts/AdminNotificationsContext";
import { playNotificationSound } from "utils/notificationSound";

export default function AdminWsListener() {
  const { addFromWs } = useAdminNotifications();
  const tokenPollRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    function getToken() {
      const raw = localStorage.getItem("auth_token");
      if (!raw) return null;
      return raw.replace(/^Bearer\s+/i, "").replace(/^"|"$/g, "");
    }

    function getWsUrl() {
      const base = (WS_ENDPOINT_URL || API_BASE_URL || "").replace(/\/api\/?$/, "");
      return WS_ENDPOINT_URL
        ? WS_ENDPOINT_URL
        : base.replace(/^http/, "ws") + "/ws/";
    }

    function handleMessage(ev) {
      // ev.data is a JSON string from server
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }

      if (msg?.type === "pong") return;

      const { event, payload } = msg || {};
      if (!event || !payload) return;

      if (event === "booking.created" || event === "booking.updated") {
        if (!payload.url) return;
        playNotificationSound();
        // ✅ push into your popover list store
        // payload might already include _id from DB
        addFromWs({ ...payload, event });

        // ✅ optional browser fallback
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "NextGen VR", {
            body: payload.body || "",
          });
        }
      }

      // Handle real-time patient form submissions
      if (event === "patient.checkin.created" || event === "patient.intake.created") {
        if (!payload.url) return;
        playNotificationSound();
        addFromWs({ ...payload, event });

        // Show browser notification
        if (Notification?.permission === "granted") {
          new Notification(payload.title || "CodeForce CRM", {
            body: payload.body || "",
          });
        }

        // Show Ant Design notification
        notification.info({
          message: payload.title || "New Patient Form",
          description: payload.body || "",
          duration: 5,
          placement: 'topRight',
        });
      }
    }

    function start() {
      const token = getToken();
      if (!token) return false;

      const wsUrl = getWsUrl();
      console.log("[ws] starting singleton on", wsUrl);

      ensureWS(wsUrl, token);

      const unsub = subscribe(handleMessage);
      return unsub;
    }

    // Try start immediately
    let cleanup = start();
    cleanupRef.current = cleanup || null;

    // If token not ready yet, poll for it for a bit
    if (!cleanup) {
      tokenPollRef.current = setInterval(() => {
        if (cleanupRef.current) return;

        const maybeCleanup = start();
        if (maybeCleanup) {
          cleanupRef.current = maybeCleanup;
          clearInterval(tokenPollRef.current);
          tokenPollRef.current = null;
        }
      }, 1000);
    }

    return () => {
      if (tokenPollRef.current) {
        clearInterval(tokenPollRef.current);
        tokenPollRef.current = null;
      }
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, [addFromWs]);

  return null;
}
